/**
 * Transactions View
 * Complete transaction list with filters and actions
 */

export class Transactions {
  constructor(container) {
    this.container = container;
    this.transactions = [];
    this.filteredTransactions = [];
    this.filters = {
      dateStart: '',
      dateEnd: '',
      type: '',
      category: '',
      subcategory: '',
      status: '',
      paymentMethod: '',
      search: ''
    };
    this.currentPage = 1;
    this.itemsPerPage = 20;
    this.updateHandler = null;
  }

  render() {
    this.container.innerHTML = `
      <div class="page-header">
        <h1 class="page-title">Transações</h1>
        <p class="page-subtitle">Gerenciar lançamentos financeiros</p>
      </div>

      <div class="card">
        <div class="card-header">
          <h2 class="card-title">Filtros</h2>
          <button class="btn btn-secondary btn-sm" id="clear-filters">Limpar Filtros</button>
        </div>
        <div class="card-body">
          <div class="filters-grid">
            <div class="form-group">
              <label for="filter-date-start">Data Inicial</label>
              <input type="date" id="filter-date-start" class="form-control">
            </div>

            <div class="form-group">
              <label for="filter-date-end">Data Final</label>
              <input type="date" id="filter-date-end" class="form-control">
            </div>

            <div class="form-group">
              <label for="filter-type">Tipo</label>
              <select id="filter-type" class="form-control">
                <option value="">Todos</option>
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
              </select>
            </div>

            <div class="form-group">
              <label for="filter-category">Categoria</label>
              <select id="filter-category" class="form-control">
                <option value="">Todas</option>
              </select>
            </div>

            <div class="form-group">
              <label for="filter-subcategory">Subcategoria</label>
              <select id="filter-subcategory" class="form-control">
                <option value="">Todas</option>
              </select>
            </div>

            <div class="form-group">
              <label for="filter-payment-method">Forma de Pagamento</label>
              <select id="filter-payment-method" class="form-control">
                <option value="">Todas</option>
                <option value="Dinheiro">Dinheiro</option>
                <option value="PIX">PIX</option>
                <option value="Cartão de Crédito">Cartão de Crédito</option>
                <option value="Cartão de Débito">Cartão de Débito</option>
                <option value="Transferência">Transferência</option>
                <option value="Boleto">Boleto</option>
                <option value="Cheque">Cheque</option>
                <option value="Outros">Outros</option>
              </select>
            </div>

            <div class="form-group">
              <label for="filter-status">Status</label>
              <select id="filter-status" class="form-control">
                <option value="">Todos</option>
                <option value="Pago">Pago</option>
                <option value="Pendente">Pendente</option>
                <option value="Atrasado">Atrasado</option>
                <option value="Parcelado">Parcelado</option>
              </select>
            </div>

            <div class="form-group">
              <label for="filter-search">Buscar</label>
              <input type="text" id="filter-search" class="form-control" placeholder="Descrição...">
            </div>
          </div>
        </div>
      </div>

      <div class="card" style="margin-top: var(--spacing-4);">
        <div class="card-header">
          <h2 class="card-title">
            <span id="transaction-count">0</span> Lançamentos
          </h2>
          <div class="card-actions">
            <button class="btn btn-secondary" id="export-csv">
              📤 Exportar CSV
            </button>
            <button class="btn btn-primary" id="new-transaction">
              ➕ Novo Lançamento
            </button>
          </div>
        </div>
        <div class="card-body" style="padding: 0;">
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Categoria</th>
                  <th>Subcategoria</th>
                  <th>Descrição</th>
                  <th>Forma de Pag.</th>
                  <th>Status</th>
                  <th class="text-right">Valor</th>
                  <th class="text-center">Ações</th>
                </tr>
              </thead>
              <tbody id="transactions-tbody">
                <tr>
                  <td colspan="9" class="text-center" style="padding: var(--spacing-8);">
                    Carregando transações...
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="card-footer" id="pagination-container">
          <!-- Pagination will be rendered here -->
        </div>
      </div>
    `;

    this.loadData();
    this.attachEventListeners();

    // Listen for transaction updates
    if (!this.updateHandler) {
      this.updateHandler = () => {
        console.log('📋 Transactions: Recarregando lista...');
        this.loadData();
      };
      window.addEventListener('transactionsUpdated', this.updateHandler);
    }
  }

  attachEventListeners() {
    // Filter listeners
    const filterInputs = this.container.querySelectorAll('.form-control');
    filterInputs.forEach(input => {
      input.addEventListener('change', () => this.applyFilters());
      if (input.type === 'text') {
        input.addEventListener('input', () => this.applyFilters());
      }
    });

    // Clear filters
    const clearBtn = this.container.querySelector('#clear-filters');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearFilters());
    }

    // New transaction
    const newBtn = this.container.querySelector('#new-transaction');
    if (newBtn) {
      newBtn.addEventListener('click', () => {
        if (typeof openModal === 'function') {
          openModal();
        }
      });
    }

    // Export CSV
    const exportBtn = this.container.querySelector('#export-csv');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => this.exportCSV());
    }

    // Type filter changes category options
    const typeFilter = this.container.querySelector('#filter-type');
    if (typeFilter) {
      typeFilter.addEventListener('change', () => this.updateCategoryFilter());
    }

    // Category filter changes subcategory options
    const categoryFilter = this.container.querySelector('#filter-category');
    if (categoryFilter) {
      categoryFilter.addEventListener('change', () => this.updateSubcategoryFilter());
    }
  }

  loadData() {
    const stored = localStorage.getItem('clinicaDaher_cashflow');
    if (stored) {
      try {
        this.transactions = JSON.parse(stored);
        this.transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
      } catch (e) {
        console.error('Error loading transactions:', e);
        this.transactions = [];
      }
    }

    this.populateCategoryFilter();
    this.applyFilters();
  }

  populateCategoryFilter() {
    const categorySelect = this.container.querySelector('#filter-category');
    if (!categorySelect) return;

    // Get unique categories from transactions
    const categories = new Set();
    this.transactions.forEach(t => {
      if (t.category) categories.add(t.category);
    });

    categorySelect.innerHTML = '<option value="">Todas</option>';
    Array.from(categories).sort().forEach(cat => {
      const option = document.createElement('option');
      option.value = cat;
      option.textContent = cat;
      categorySelect.appendChild(option);
    });
  }

  updateCategoryFilter() {
    const type = this.container.querySelector('#filter-type')?.value;
    const categorySelect = this.container.querySelector('#filter-category');
    if (!categorySelect) return;

    categorySelect.innerHTML = '<option value="">Todas</option>';

    if (type) {
      const categories = this.transactions
        .filter(t => t.type === type)
        .map(t => t.category)
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .sort();

      categories.forEach(cat => {
        const option = document.createElement('option');
        option.value = cat;
        option.textContent = cat;
        categorySelect.appendChild(option);
      });
    } else {
      this.populateCategoryFilter();
    }

    this.updateSubcategoryFilter();
  }

  updateSubcategoryFilter() {
    const category = this.container.querySelector('#filter-category')?.value;
    const subcategorySelect = this.container.querySelector('#filter-subcategory');
    if (!subcategorySelect) return;

    subcategorySelect.innerHTML = '<option value="">Todas</option>';

    if (category) {
      const subcategories = this.transactions
        .filter(t => t.category === category)
        .map(t => t.subcategory)
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .sort();

      subcategories.forEach(sub => {
        const option = document.createElement('option');
        option.value = sub;
        option.textContent = sub;
        subcategorySelect.appendChild(option);
      });
    }
  }

  applyFilters() {
    // Read filter values
    this.filters.dateStart = this.container.querySelector('#filter-date-start')?.value || '';
    this.filters.dateEnd = this.container.querySelector('#filter-date-end')?.value || '';
    this.filters.type = this.container.querySelector('#filter-type')?.value || '';
    this.filters.category = this.container.querySelector('#filter-category')?.value || '';
    this.filters.subcategory = this.container.querySelector('#filter-subcategory')?.value || '';
    this.filters.status = this.container.querySelector('#filter-status')?.value || '';
    this.filters.paymentMethod = this.container.querySelector('#filter-payment-method')?.value || '';
    this.filters.search = this.container.querySelector('#filter-search')?.value.toLowerCase() || '';

    // Apply filters
    this.filteredTransactions = this.transactions.filter(t => {
      if (this.filters.dateStart && t.date < this.filters.dateStart) return false;
      if (this.filters.dateEnd && t.date > this.filters.dateEnd) return false;
      if (this.filters.type && t.type !== this.filters.type) return false;
      if (this.filters.category && t.category !== this.filters.category) return false;
      if (this.filters.subcategory && t.subcategory !== this.filters.subcategory) return false;
      if (this.filters.status && t.status !== this.filters.status) return false;
      if (this.filters.paymentMethod && t.paymentMethod !== this.filters.paymentMethod) return false;
      if (this.filters.search && !t.description?.toLowerCase().includes(this.filters.search)) return false;
      return true;
    });

    this.currentPage = 1;
    this.renderTable();
    this.renderPagination();
  }

  clearFilters() {
    this.container.querySelector('#filter-date-start').value = '';
    this.container.querySelector('#filter-date-end').value = '';
    this.container.querySelector('#filter-type').value = '';
    this.container.querySelector('#filter-category').value = '';
    this.container.querySelector('#filter-subcategory').value = '';
    this.container.querySelector('#filter-status').value = '';
    this.container.querySelector('#filter-payment-method').value = '';
    this.container.querySelector('#filter-search').value = '';

    this.applyFilters();
  }

  renderTable() {
    const tbody = this.container.querySelector('#transactions-tbody');
    const countEl = this.container.querySelector('#transaction-count');

    if (!tbody) return;

    if (countEl) {
      countEl.textContent = this.filteredTransactions.length;
    }

    if (this.filteredTransactions.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="9" class="text-center" style="padding: var(--spacing-8); color: var(--gray-500);">
            Nenhuma transação encontrada
          </td>
        </tr>
      `;
      return;
    }

    // Pagination
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    const pageTransactions = this.filteredTransactions.slice(start, end);

    tbody.innerHTML = pageTransactions.map(t => `
      <tr>
        <td>${this.formatDate(t.date)}</td>
        <td>
          <span class="badge badge-${t.type === 'entrada' ? 'success' : 'danger'}">
            ${t.type === 'entrada' ? '↑ Entrada' : '↓ Saída'}
          </span>
        </td>
        <td>${t.category || '-'}</td>
        <td>${t.subcategory || '-'}</td>
        <td>${t.description || '-'}</td>
        <td>${t.paymentMethod || '-'}</td>
        <td>
          ${t.status ? `<span class="badge badge-${this.getStatusColor(t.status)}">${t.status}</span>` : '-'}
        </td>
        <td class="text-right" style="color: var(--color-${t.type === 'entrada' ? 'success' : 'danger'}-dark); font-weight: 600;">
          ${t.type === 'entrada' ? '+' : '-'} ${this.formatCurrency(t.amount)}
        </td>
        <td class="text-center">
          <div class="btn-group">
            <button class="btn btn-sm btn-secondary" onclick="openModal('${t.id}')" title="Editar">
              ✏️
            </button>
            <button class="btn btn-sm btn-danger" onclick="deleteTransaction('${t.id}')" title="Excluir">
              🗑️
            </button>
          </div>
        </td>
      </tr>
    `).join('');
  }

  renderPagination() {
    const container = this.container.querySelector('#pagination-container');
    if (!container) return;

    const totalPages = Math.ceil(this.filteredTransactions.length / this.itemsPerPage);

    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    container.innerHTML = `
      <div class="pagination">
        <button class="btn btn-sm btn-secondary" ${this.currentPage === 1 ? 'disabled' : ''} id="page-prev">
          ← Anterior
        </button>
        <span class="pagination-info">
          Página ${this.currentPage} de ${totalPages}
        </span>
        <button class="btn btn-sm btn-secondary" ${this.currentPage === totalPages ? 'disabled' : ''} id="page-next">
          Próxima →
        </button>
      </div>
    `;

    // Attach pagination events
    const prevBtn = container.querySelector('#page-prev');
    const nextBtn = container.querySelector('#page-next');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (this.currentPage > 1) {
          this.currentPage--;
          this.renderTable();
          this.renderPagination();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (this.currentPage < totalPages) {
          this.currentPage++;
          this.renderTable();
          this.renderPagination();
        }
      });
    }
  }

  exportCSV() {
    if (this.filteredTransactions.length === 0) {
      alert('Nenhuma transação para exportar.');
      return;
    }

    // CSV Header
    let csv = 'Data,Tipo,Categoria,Subcategoria,Descrição,Forma de Pagamento,Status,Valor\n';

    // CSV Rows
    this.filteredTransactions.forEach(t => {
      const date = this.formatDate(t.date);
      const type = t.type === 'entrada' ? 'Entrada' : 'Saída';
      const description = (t.description || '').replace(/,/g, ';');
      const amount = this.formatNumberForCSV(t.amount);

      csv += `${date},${type},${t.category || ''},"${t.subcategory || ''}","${description}",${t.paymentMethod || ''},${t.status || ''},${amount}\n`;
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const today = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `transacoes-${today}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    alert(`✅ ${this.filteredTransactions.length} transações exportadas!`);
  }

  getStatusColor(status) {
    const colors = {
      'Pago': 'success',
      'Pendente': 'warning',
      'Atrasado': 'danger',
      'Parcelado': 'info'
    };
    return colors[status] || 'secondary';
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  formatNumberForCSV(value) {
    return value.toFixed(2).replace('.', ',');
  }

  formatDate(dateString) {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  destroy() {
    if (this.updateHandler) {
      window.removeEventListener('transactionsUpdated', this.updateHandler);
      this.updateHandler = null;
    }
  }
}
